import { useEffect, useState } from 'react';
import type { FocusEvent } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';
import { useStyles } from '../../styles';
import '../../styles/enhanceTestPlanModal.css';
import '../../styles/addProjectModal.css';
import type { EnhancementTestPlanSelection } from '../../interfaces/ui';
import { useMappedProjects } from '../../hooks/useMappedProjects';
import { useProjectTestPlans } from '../../hooks/useProjectTestPlans';
import { useProjectTestSuites } from '../../hooks/useProjectTestSuites';
import { useFormValidation } from '../../hooks/useFormValidation';
import FormRadioGroup from '../AddProjectModal/FormRadioGroup';
import { FormSelect } from '../AddProjectModal/FormSelect';
import TestPlanAndSuite from '../AddProjectModal/TestPlanAndSuite';
import {
  getMappedProjectPlaceHolderText,
  getTestSuiteOptions,
} from '../AddProjectModal/utils/helpers';
import {
  DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION,
  ENHANCEMENT_TEST_CASES_LABEL,
  PROJECT_MAPPING_OPTIONS,
} from '../../constants';

interface EnhanceTestPlanModalProps {
  open: boolean;
  apmNumber: string;
  onClose: () => void;
  onRunTests: (
    userPrompt: string,
    selection?: EnhancementTestPlanSelection,
  ) => void;
}

export function EnhanceTestPlanModal({
  open,
  apmNumber,
  onClose,
  onRunTests,
}: EnhanceTestPlanModalProps) {
  const classes = useStyles();
  const [userPrompt, setUserPrompt] = useState('');
  const [includeAdditional, setIncludeAdditional] = useState(false);
  const [selection, setSelection] = useState(
    DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION,
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { validateField } = useFormValidation();
  const {
    mappedProjects,
    loadingMappedProjects,
    mappedProjectsError,
    fetchMappedProjects,
  } = useMappedProjects();
  const { testPlans, loadingTestPlans, fetchProjectTestPlans } =
    useProjectTestPlans();
  const { testSuites, loadingTestSuites, fetchProjectTestSuites } =
    useProjectTestSuites();

  const mappedProject = mappedProjects.find(
    project => project.projectId === selection.mappedProjectId,
  );
  const mappedProjectOptions = mappedProjects.map(project => ({
    value: project.projectId,
    label: project.projectName,
  }));
  const testPlanOptions = selection.mappedProjectId
    ? testPlans.map(plan => ({ value: plan.id.toString(), label: plan.name }))
    : [];
  const testSuiteOptions = getTestSuiteOptions(
    selection.projectMappingType,
    selection.testPlanId,
    testPlans,
    testSuites,
  );

  useEffect(() => {
    setIncludeAdditional(false);
    setSelection(DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION);
    setTouched({});
    setErrors({});
  }, [open, apmNumber]);

  useEffect(() => {
    fetchMappedProjects(
      apmNumber,
      open && includeAdditional ? selection.projectMappingType : '',
    );
  }, [
    apmNumber,
    open,
    includeAdditional,
    selection.projectMappingType,
    fetchMappedProjects,
  ]);

  useEffect(() => {
    fetchProjectTestPlans(
      apmNumber,
      open && includeAdditional && mappedProject
        ? mappedProject
        : { projectId: '', projectName: '', projectUrl: '' },
      selection.projectMappingType,
    );
  }, [
    apmNumber,
    open,
    includeAdditional,
    mappedProject,
    selection.projectMappingType,
    fetchProjectTestPlans,
  ]);

  useEffect(() => {
    const shouldFetch =
      open &&
      includeAdditional &&
      selection.projectMappingType === 'ADO' &&
      Boolean(mappedProject);
    fetchProjectTestSuites(
      apmNumber,
      shouldFetch ? selection.mappedProjectName : '',
      shouldFetch ? selection.mappedProjectOrg : '',
      shouldFetch ? selection.testPlanId : '',
      shouldFetch ? selection.testPlanName : '',
    );
  }, [
    apmNumber,
    open,
    includeAdditional,
    mappedProject,
    selection,
    fetchProjectTestSuites,
  ]);

  const handleFieldBlur = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const name = event.target.name as keyof EnhancementTestPlanSelection;
    if (!name) return;
    setTouched(previous => ({ ...previous, [name]: true }));
    setErrors(previous => ({
      ...previous,
      [name]: validateField(name, selection[name]),
    }));
  };

  const hasCompleteSelection = Boolean(
    selection.projectMappingType &&
      mappedProject &&
      testPlanOptions.some(option => option.value === selection.testPlanId) &&
      testSuiteOptions.some(option => option.value === selection.testSuiteId) &&
      !loadingMappedProjects &&
      !loadingTestPlans &&
      !loadingTestSuites,
  );
  const canSubmit =
    Boolean(userPrompt.trim()) && (!includeAdditional || hasCompleteSelection);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: `${classes.enhanceTestPlanPaper} ets-paper` }}
    >
      <DialogTitle disableTypography className="ets-header">
        <Typography variant="h6" className="ets-header-title">
          Enhance Test Plan
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          className="ets-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="ets-content">
        <Typography variant="body2" className="ets-description">
          Describe how you would like the AI to improve the existing test suite.
          Additional test cases may be generated, refined, deduplicated, and
          executed automatically.
        </Typography>
        <Box className="ets-form-group">
          <Typography component="label" className="ets-label">
            Enhancement Request
            <span className="ets-required">*</span>
            <span className="ets-required-hint">
              (Enter a prompt to continue.)
            </span>
          </Typography>
          <TextField
            multiline
            rows={5}
            variant="outlined"
            fullWidth
            placeholder={`Examples:\n\n• Generate 20 additional test cases for checkout scenarios\n\n• Add negative test scenarios\n\n• Include accessibility validations`}
            value={userPrompt}
            onChange={e => setUserPrompt(e.target.value)}
            className="ets-textarea"
          />
          <Typography className="ets-helper-text">
            The AI will use these instructions to enhance the most recent test
            suite before starting a new execution run.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={includeAdditional}
              onChange={event => {
                setIncludeAdditional(event.target.checked);
                setSelection(DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION);
                setTouched({});
                setErrors({});
              }}
            />
          }
          label={ENHANCEMENT_TEST_CASES_LABEL}
        />
        {includeAdditional && (
          <section className="ets-source-testplan-section">
            <FormRadioGroup
              id="projectMappingType"
              label="Source Test Plan"
              value={selection.projectMappingType}
              options={PROJECT_MAPPING_OPTIONS}
              required
              onBlur={handleFieldBlur}
              error={
                touched.projectMappingType
                  ? errors.projectMappingType
                  : undefined
              }
              onChange={event => {
                const provider = event.target.value;
                if (provider !== 'ADO' && provider !== 'JIRA') return;
                setSelection({
                  ...DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION,
                  projectMappingType: provider,
                });
                setTouched({});
                setErrors({});
              }}
            />
            <Typography className="ets-helper-text">
              Select ADO or JIRA to continue
            </Typography>
            {selection.projectMappingType && (
              <>
                <FormSelect
                  id="mappedProjectName"
                  label="Mapped Project"
                  value={selection.mappedProjectId}
                  options={mappedProjectOptions}
                  required
                  loading={loadingMappedProjects}
                  disabled={loadingMappedProjects}
                  hasNoData={
                    !loadingMappedProjects && mappedProjects.length === 0
                  }
                  placeholder={getMappedProjectPlaceHolderText(
                    selection.projectMappingType,
                    loadingMappedProjects,
                    mappedProjects.length,
                  )}
                  error={
                    mappedProjectsError ||
                    (touched.mappedProjectName
                      ? errors.mappedProjectName
                      : undefined)
                  }
                  onBlur={handleFieldBlur}
                  onChange={event => {
                    const project = mappedProjects.find(
                      option => option.projectId === event.target.value,
                    );
                    setSelection(previous => ({
                      ...DEFAULT_ENHANCEMENT_TEST_PLAN_SELECTION,
                      projectMappingType: previous.projectMappingType,
                      mappedProjectId: project?.projectId || '',
                      mappedProjectName: project?.projectName || '',
                      mappedProjectOrg: project?.projectOrg || '',
                    }));
                    setTouched({});
                    setErrors({});
                  }}
                />
                <TestPlanAndSuite
                  projectMappingType={selection.projectMappingType}
                  mappedProjectId={selection.mappedProjectId}
                  testPlanId={selection.testPlanId}
                  testSuiteId={selection.testSuiteId}
                  testPlanOptions={testPlanOptions}
                  testSuiteOptions={testSuiteOptions}
                  touched={touched}
                  errors={errors}
                  isEditing={false}
                  loadingTestPlans={loadingTestPlans}
                  loadingTestSuites={loadingTestSuites}
                  required
                  existing
                  onFieldBlur={handleFieldBlur}
                  onTestPlanChange={value => {
                    const plan = testPlanOptions.find(
                      option => option.value === value,
                    );
                    setSelection(previous => ({
                      ...previous,
                      testPlanId: plan?.value || '',
                      testPlanName: plan?.label || '',
                      testSuiteId: '',
                      testSuiteName: '',
                    }));
                    setTouched(previous => ({
                      ...previous,
                      testPlanName: false,
                      testSuiteName: false,
                    }));
                    setErrors(previous => ({
                      ...previous,
                      testPlanName: '',
                      testSuiteName: '',
                    }));
                  }}
                  onTestSuiteChange={value => {
                    const suite = testSuiteOptions.find(
                      option => option.value === value,
                    );
                    setSelection(previous => ({
                      ...previous,
                      testSuiteId: suite?.value || '',
                      testSuiteName: suite?.label || '',
                    }));
                    setErrors(previous => ({ ...previous, testSuiteName: '' }));
                  }}
                />
              </>
            )}
          </section>
        )}
      </DialogContent>
      <DialogActions className="ets-actions">
        <Button variant="outlined" className="ets-cancel-btn" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="ets-submit-btn"
          disabled={!canSubmit}
          onClick={() => {
            if (canSubmit) {
              onRunTests(userPrompt, includeAdditional ? selection : undefined);
              onClose();
            }
          }}
        >
          Enhance & Execute
        </Button>
      </DialogActions>
    </Dialog>
  );
}
